import { debounce, throttle } from '@modules/helpers';
import { getScrollableParent } from '@modules/screen-helpers';
import { useEffect, useState } from 'react';

/**
 * Get access to a nodes DOMRect object that provides information about the size and position of the
 * element. This object will be updated when the screen is scrolled or resized, so we can accurately track
 * the element over time. Keep in mind this relies on getBoundingClientRect which will trigger the browser
 * to syncronously calculate the style and layout (i.e. reflow). As a result make sure that this is only active
 * when necessary.
 * @param targetNode The node whose measurements we would like to track
 * @param measure When false no measurements will be taken, even if the screen changes
 */
export default function useResponsiveRect(
  targetNode: HTMLElement | undefined,
  measure = false,
): DOMRect | undefined {
  const [rect, setRect] = useState<DOMRect>();

  useEffect(() => {
    if (!measure || !targetNode) return;

    function handleRectUpdate() {
      if (targetNode) {
        const rect = targetNode.getBoundingClientRect();

        setRect(rect);
      }
    }

    const throttledResize = throttle(handleRectUpdate, 100);
    const debouncedScroll = debounce(handleRectUpdate, 100);

    // The popover may sit inside a scrollable parent which means we can't rely on detecting
    // scroll events on the body, we need to detect scrollable parents and listen to their events.
    const scrollableContainer = getScrollableParent(targetNode);

    if (!scrollableContainer) return;

    scrollableContainer.addEventListener('resize', throttledResize);
    scrollableContainer.addEventListener('scroll', debouncedScroll);

    // This is the initial measurement, the event listeners are only responsible for updating the
    // measurement if something changes from this point onwards.
    handleRectUpdate();

    return () => {
      scrollableContainer.removeEventListener('resize', throttledResize);
      scrollableContainer.removeEventListener('scroll', debouncedScroll);
    };
  }, [measure, targetNode]);

  useEffect(() => {
    if (!measure && rect) setRect(undefined);
  }, [measure, rect]);

  return rect;
}
